import { v4 } from 'uuid';
import { delay, fetchWithTimeout, noop } from './utils';

type TrackInit = Pick<RequestInit, 'method' | 'headers' | 'body'>;

export class PixelManager {
  private readonly imgPixelManager;
  private readonly fetchPixelManager;

  constructor(type: 'http') {
    switch (type) {
      case 'http':
        this.imgPixelManager = new ImgPixelManager();
        this.fetchPixelManager = new FetchPixelManager();
        break;
    }
  }

  track(
    url: string,
    {
      options = {},
      timeout = 10_000,
    }: { options?: Pick<RequestInit, 'method' | 'headers' | 'body'>; timeout: number | undefined },
  ): Promise<Response> {
    const { method = 'get', headers, body } = options;

    if (method === 'get') {
      return new Promise((resolve, reject) => {
        this.imgPixelManager.setSrc(url, resolve, reject, timeout);
      });
    }

    return this.fetchPixelManager.track(url, { method, headers, body }, timeout);
  }
}

const DELAY = 200;

class ImgPixelManager {
  pixel = new Image();
  promiseChain = Promise.resolve();
  isListenerCalled = false;
  onError: (...args: unknown[]) => unknown = noop;

  set src(src: string) {
    this.promiseChain = this.promiseChain
      .then(() => {
        this.pixel.src = src;
      })
      .then(() => delay(DELAY));
  }

  setSrc(src: string, onload = noop, onError = noop, timeout: number) {
    this.promiseChain = this.promiseChain
      .then(() => {
        if (!this.isListenerCalled && this.onError) this.onError();
        this.isListenerCalled = false;
        this.onError = onError;

        const timeoutId = setTimeout(() => {
          this.pixel.src = '';
          onError(new Error('Request timed out'));
        }, timeout);

        const onPixelError = (...args: unknown[]) => {
          clearTimeout(timeoutId);
          onError.apply(this, Array.from(args));
        };

        const onPixelLoad = (...args: unknown[]) => {
          clearTimeout(timeoutId);
          onload(...args);
        };

        this.pixel.onload = this.listenerCalled(onPixelLoad);
        this.pixel.onerror = this.listenerCalled(onPixelError);
        this.pixel.src = src;
      })
      .then(() => delay(DELAY));
  }

  private listenerCalled(listener: (...args: any[]) => void) {
    return (...args: any[]) => {
      this.isListenerCalled = true;
      listener(...args);
    };
  }
}

class FetchPixelManager {
  private readonly requestInit: RequestInit = {
    method: 'post',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'x-request-id': v4() },
  };

  track(url: string, trackInit: TrackInit, timeout: number) {
    return fetchWithTimeout(url, {
      ...this.requestInit,
      ...trackInit,
      headers: { ...this.requestInit.headers, ...trackInit.headers },
      timeout,
    }).then(response => {
      if (!response.ok) throw new Error(response.statusText);
      return response;
    });
  }
}
